import type { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  category: 'curiosidades',
  author: 'Ivan Henrique',
  publishedAt: '2026-03-10',
  coverImage: '/blog/bolao-entre-amigos.svg',
  tags: ['Bolão', 'Tradição', 'História', 'Cultura'],
  i18n: {
    'pt-BR': {
      slug: 'bolao-entre-amigos',
      title: 'Bolão entre amigos: a tradição brasileira que virou plataforma online',
      excerpt:
        'Da Copa do Mundo de 1970 ao app no celular, o bolão entre amigos é uma das tradições mais queridas do futebol brasileiro. Conheça a evolução desse hábito tão nosso.',
      readTimeMinutes: 6,
      coverImageAlt: 'Capa: Bolão entre amigos — tradição brasileira',
      keywords: ['bolão entre amigos', 'tradição brasileira', 'história bolão', 'evolução plataforma'],
      content: (
        <>
          <p>
            Quem cresceu no Brasil dos anos 1980 ou 1990 lembra: chegando a
            Copa do Mundo, alguém do grupo de amigos (ou da família, ou do
            trabalho) puxava o bolão. Cada um tinha uma folha amarela
            com a tabela de jogos, pagava um valor simbólico, anotava os
            palpites a caneta, e o organizador conferia os resultados a
            cada jogo. Era uma maratona de 30 dias com pontuação calculada
            no papel pardo.
          </p>
          <p>
            Esse hábito atravessou décadas. Sobreviveu à internet discada,
            ao Excel, ao WhatsApp e finalmente chegou aos aplicativos
            modernos. Mas em essência, o bolão brasileiro continua sendo a
            mesma coisa: <strong>uma desculpa pra reunir amigos em torno do
            futebol</strong>.
          </p>

          <h2>A origem: Copa do Mundo de 1970 (e antes)</h2>
          <p>
            O bolão brasileiro como tradição familiar e entre amigos se
            consolida nos anos 1970, especialmente na Copa do México de
            1970, quando o Brasil conquistou o tricampeonato com Pelé. A
            cobertura televisiva ampla e o sucesso da seleção criaram o
            ambiente perfeito pra família e amigos se reunirem em torno do
            torneio.
          </p>
          <p>
            Naquela época, bolões eram organizados em papel. <strong>Cada
            participante recebia uma folha com a tabela de jogos e
            palpitava placar a placar</strong>. O organizador (geralmente
            a pessoa mais &quot;contadora&quot; do grupo, ou o
            professor de matemática da escola) calculava pontos a cada
            rodada.
          </p>

          <h2>Os anos 1980 e 1990: o auge do papel</h2>
          <p>
            Com Copa após Copa, o bolão virou tradição estabelecida. Cada
            Copa do Mundo (1982, 1986, 1990, 1994) tinha sua versão. Aos
            poucos, começaram a aparecer também:
          </p>
          <ul>
            <li>
              <strong>Bolões de Brasileirão:</strong> mais longos, mais
              desafiadores. Geralmente em grupos de trabalho.
            </li>
            <li>
              <strong>Bolões da Champions Conmebol e Libertadores:</strong> menos
              populares, mas existiam para o público mais entusiasta.
            </li>
            <li>
              <strong>Bolões de Eurocopa:</strong> raros, mas existentes
              entre torcedores de selecionados europeus específicos.
            </li>
          </ul>

          <h2>A era do Excel (anos 2000)</h2>
          <p>
            Com a popularização do computador pessoal e do Microsoft
            Office, os bolões migraram pro Excel. Surgiu o papel do
            &quot;coordenador da planilha&quot; — aquele amigo que mandava
            a planilha pra todo mundo preencher, copiava os palpites de
            volta, calculava pontuação com fórmulas.
          </p>
          <p>
            <strong>Era um avanço enorme em relação ao papel, mas tinha
            problemas</strong>: planilha versão Excel quebrada, palpites
            perdidos, fórmulas erradas. O coordenador ganhava também o
            papel ingrato de explicar a pontuação a cada rodada.
          </p>

          <h2>A era do WhatsApp (2010s)</h2>
          <p>
            Com o WhatsApp, o bolão migrou para o grupo. Cada um postava
            os palpites no grupo, o organizador anotava em planilha, e
            depois compartilhava o ranking. <strong>Era ainda mais ágil
            que Excel mas o caos crescia</strong> — palpites perdidos
            entre 200 mensagens, dúvidas de quem palpitou o quê, alguém
            que sempre &quot;esquecia&quot; de palpitar e depois reclamava.
          </p>

          <h2>A era dos apps (a partir de 2010s e 2020s)</h2>
          <p>
            A partir de meados dos anos 2010, começaram a surgir apps e
            sites especializados em bolão. <strong>O que eles entregam é
            simples mas valioso</strong>: importação automática de jogos,
            cálculo automático de pontuação, ranking em tempo real,
            notificações de lembrete, ranking global entre todos os
            usuários do app.
          </p>
          <p>
            O <strong>Bolão Elite</strong> é parte dessa última geração.
            Foi criado em 2026 por um Engenheiro de Software brasileiro (fã de
            futebol e jogador habitual de bolão entre amigos) que queria
            resolver definitivamente os problemas das gerações anteriores.
            Saiba mais na <a href="/sobre">página sobre nós</a>.
          </p>

          <h2>O que NÃO mudou</h2>
          <p>
            Apesar de toda evolução tecnológica, o coração do bolão é o
            mesmo. <strong>Continua sendo sobre amigos reunidos em torno
            do futebol</strong>. As conversas no boteco, as zoações pelo
            WhatsApp, os palpites loucos pra zoar quem aposta no time
            rival, a celebração na hora do gol — tudo isso continua.
          </p>
          <p>
            O app é só ferramenta. <strong>A tradição é cultural</strong>,
            e o Brasil é provavelmente o país que mais a abraçou.
          </p>

          <h2>O que mudou (pra melhor)</h2>
          <p>
            Por outro lado, a era digital trouxe vantagens reais:
          </p>
          <ul>
            <li>
              <strong>Sem erro de cálculo:</strong> antes, o organizador
              podia errar a planilha sem querer. Hoje a pontuação é
              automática — não tem espaço pra polêmica.
            </li>
            <li>
              <strong>Sem palpite perdido:</strong> tudo registrado
              digitalmente, com timestamp.
            </li>
            <li>
              <strong>Notificações:</strong> ninguém esquece de palpitar
              (a menos que ignore a notificação).
            </li>
            <li>
              <strong>Ranking em tempo real:</strong> não precisa esperar
              o organizador &quot;fechar a rodada&quot;.
            </li>
            <li>
              <strong>Bolão público:</strong> agora dá pra competir não
              só com amigos mas com qualquer um do Brasil.
            </li>
          </ul>

          <h2>O futuro</h2>
          <p>
            O bolão entre amigos vai continuar. Em Copa de 2026, em
            Brasileirão a cada ano, em qualquer torneio relevante. <strong>É
            uma tradição que faz parte da cultura brasileira do
            futebol</strong>, e nenhuma ferramenta consegue (ou deve)
            substituir o aspecto humano disso.
          </p>
          <p>
            O Bolão Elite só quer tornar essa experiência mais fácil. Sem
            planilha, sem cálculo, sem confusão. Você junta os amigos,
            cria o bolão, palpita, acompanha o ranking. O resto é
            futebol.
          </p>

          <h2>Conclusão</h2>
          <p>
            De papel pardo a aplicativo, o bolão brasileiro caminhou junto
            com a tecnologia. Mas no fundo, continua sendo sobre o que
            sempre foi: amigos, futebol, alegria, zoação. Nenhum app vai
            mudar isso — e nenhum app deveria querer mudar.
          </p>
          <p>
            Pronto pra criar o seu? Veja a{' '}
            <a href="/">página inicial</a> ou explore o{' '}
            <a href="/blog">blog completo</a> para mais conteúdo.
          </p>
        </>
      ),
    },
    en: {
      slug: 'football-pool-with-friends',
      title: 'The football pool with friends: the Brazilian tradition that became an online platform',
      excerpt:
        'From the 1970 World Cup to the app on your phone, the football pool with friends is one of the most beloved traditions of Brazilian football. Discover how this very Brazilian habit evolved.',
      readTimeMinutes: 6,
      coverImageAlt: 'Cover: The football pool with friends — a Brazilian tradition',
      coverImage: '/blog/bolao-entre-amigos-en.svg',
      keywords: ['football pool with friends', 'brazilian tradition', 'pool history', 'platform evolution'],
      content: (
        <>
          <p>
            Anyone who grew up in 1980s or 1990s Brazil remembers it: as the
            World Cup arrived, someone in the group of friends (or in the
            family, or at work) would get the pool going. Each person had a
            yellow sheet with the match schedule, paid a token amount, wrote
            the predictions in pen, and the organiser checked the results after
            each match. It was a 30-day marathon with the points worked out on
            brown paper.
          </p>
          <p>
            That habit crossed decades. It survived dial-up internet, Excel,
            WhatsApp and finally reached modern apps. But in essence, the
            Brazilian pool is still the same thing: <strong>an excuse to bring
            friends together around football</strong>.
          </p>

          <h2>The origin: the 1970 World Cup (and before)</h2>
          <p>
            The Brazilian pool as a family and friends tradition took hold in
            the 1970s, especially at the 1970 World Cup in Mexico, when Brazil
            won its third title with Pelé. The wide television coverage and the
            success of the national team created the perfect setting for family
            and friends to gather around the tournament.
          </p>
          <p>
            Back then, pools were organised on paper. <strong>Each
            participant received a sheet with the match schedule and predicted
            score by score</strong>. The organiser (usually the most
            &quot;number-crunching&quot; person in the group, or the school's
            maths teacher) worked out the points after each matchday.
          </p>

          <h2>The 1980s and 1990s: the peak of paper</h2>
          <p>
            World Cup after World Cup, the pool became an established
            tradition. Every World Cup (1982, 1986, 1990, 1994) had its
            version. Little by little, others started to appear too:
          </p>
          <ul>
            <li>
              <strong>Brazilian league pools:</strong> longer, more
              challenging. Usually in workplace groups.
            </li>
            <li>
              <strong>Copa Libertadores pools:</strong> less
              popular, but they existed for the more enthusiastic crowd.
            </li>
            <li>
              <strong>Euro pools:</strong> rare, but they existed
              among fans of specific European national teams.
            </li>
          </ul>

          <h2>The Excel era (the 2000s)</h2>
          <p>
            With the spread of the personal computer and Microsoft
            Office, pools migrated to Excel. The role of the
            &quot;spreadsheet coordinator&quot; emerged — that friend who sent
            the spreadsheet around for everyone to fill in, copied the
            predictions back, and worked out the points with formulas.
          </p>
          <p>
            <strong>It was a huge step up from paper, but it had
            problems</strong>: a broken Excel version of the spreadsheet, lost
            predictions, wrong formulas. The coordinator also got the
            thankless job of explaining the points after each matchday.
          </p>

          <h2>The WhatsApp era (the 2010s)</h2>
          <p>
            With WhatsApp, the pool moved to the group chat. Everyone posted
            their predictions in the group, the organiser noted them down in a
            spreadsheet, and then shared the leaderboard. <strong>It was even
            quicker than Excel but the chaos grew</strong> — predictions lost
            among 200 messages, doubts about who predicted what, someone who
            always &quot;forgot&quot; to predict and then complained.
          </p>

          <h2>The app era (from the 2010s and 2020s onwards)</h2>
          <p>
            From the mid-2010s, apps and websites specialised in pools started
            to appear. <strong>What they deliver is simple but
            valuable</strong>: automatic import of matches, automatic point
            calculation, a real-time leaderboard, reminder notifications, a
            global leaderboard across all the app's users.
          </p>
          <p>
            <strong>Bolão Elite</strong> is part of that latest generation.
            It was created in 2026 by a Brazilian Software Engineer (a football
            fan and regular player of pools with friends) who wanted to solve
            once and for all the problems of the previous generations.
            Find out more on the <a href="/en/about">about us page</a>.
          </p>

          <h2>What did NOT change</h2>
          <p>
            Despite all the technological evolution, the heart of the pool is
            the same. <strong>It is still about friends gathered around
            football</strong>. The bar conversations, the banter over
            WhatsApp, the crazy predictions made to wind up whoever bets on the
            rival team, the celebration when a goal goes in — all of that
            continues.
          </p>
          <p>
            The app is just a tool. <strong>The tradition is cultural</strong>,
            and Brazil is probably the country that has embraced it the most.
          </p>

          <h2>What did change (for the better)</h2>
          <p>
            On the other hand, the digital era brought real advantages:
          </p>
          <ul>
            <li>
              <strong>No calculation errors:</strong> before, the organiser
              could accidentally get the spreadsheet wrong. Today the points
              are automatic — there is no room for controversy.
            </li>
            <li>
              <strong>No lost predictions:</strong> everything recorded
              digitally, with a timestamp.
            </li>
            <li>
              <strong>Notifications:</strong> nobody forgets to make their
              predictions (unless they ignore the notification).
            </li>
            <li>
              <strong>Real-time leaderboard:</strong> no need to wait for
              the organiser to &quot;close the matchday&quot;.
            </li>
            <li>
              <strong>Public pool:</strong> now you can compete not
              only with friends but with anyone in Brazil.
            </li>
          </ul>

          <h2>The future</h2>
          <p>
            The pool with friends will continue. At the 2026 World Cup, in the
            Brazilian league every year, at any relevant tournament. <strong>It
            is a tradition that is part of Brazilian football
            culture</strong>, and no tool can (or should) replace the human
            side of it.
          </p>
          <p>
            Bolão Elite just wants to make that experience easier. No
            spreadsheet, no calculation, no confusion. You gather your friends,
            create the pool, make predictions, follow the leaderboard. The rest
            is football.
          </p>

          <h2>Conclusion</h2>
          <p>
            From brown paper to an app, the Brazilian pool has walked alongside
            technology. But deep down, it is still about what it always was:
            friends, football, joy, banter. No app will change that — and no
            app should want to change it.
          </p>
          <p>
            Ready to create yours? See the{' '}
            <a href="/en">home page</a> or explore the{' '}
            <a href="/en/blog">full blog</a> for more content.
          </p>
        </>
      ),
    },
    es: {
      slug: 'quiniela-entre-amigos',
      title: 'La quiniela entre amigos: la tradición brasileña que se volvió plataforma online',
      excerpt:
        'Del Mundial de 1970 a la app en el celular, la quiniela entre amigos es una de las tradiciones más queridas del fútbol brasileño. Conoce la evolución de este hábito tan nuestro.',
      readTimeMinutes: 6,
      coverImageAlt: 'Portada: La quiniela entre amigos — tradición brasileña',
      coverImage: '/blog/bolao-entre-amigos-es.svg',
      keywords: ['quiniela entre amigos', 'tradición brasileña', 'historia quiniela', 'evolución plataforma'],
      content: (
        <>
          <p>
            Quien creció en el Brasil de los años 1980 o 1990 lo recuerda: al
            llegar el Mundial, alguien del grupo de amigos (o de la familia, o
            del trabajo) ponía en marcha la quiniela. Cada uno tenía una hoja
            amarilla con el calendario de partidos, pagaba un valor simbólico,
            anotaba los pronósticos a lápiz, y el organizador comprobaba los
            resultados después de cada partido. Era una maratón de 30 días con
            la puntuación calculada en papel madera.
          </p>
          <p>
            Ese hábito atravesó décadas. Sobrevivió a internet por línea
            telefónica, a Excel, a WhatsApp y finalmente llegó a las
            aplicaciones modernas. Pero en esencia, la quiniela brasileña sigue
            siendo lo mismo: <strong>una excusa para reunir amigos en torno al
            fútbol</strong>.
          </p>

          <h2>El origen: el Mundial de 1970 (y antes)</h2>
          <p>
            La quiniela brasileña como tradición familiar y entre amigos se
            consolida en los años 1970, especialmente en el Mundial de México
            de 1970, cuando Brasil conquistó el tricampeonato con Pelé. La
            amplia cobertura televisiva y el éxito de la selección crearon el
            ambiente perfecto para que la familia y los amigos se reunieran en
            torno al torneo.
          </p>
          <p>
            En aquella época, las quinielas se organizaban en papel. <strong>Cada
            participante recibía una hoja con el calendario de partidos y
            pronosticaba marcador a marcador</strong>. El organizador (generalmente
            la persona más &quot;contadora&quot; del grupo, o el
            profesor de matemáticas de la escuela) calculaba los puntos en cada
            jornada.
          </p>

          <h2>Los años 1980 y 1990: el apogeo del papel</h2>
          <p>
            Mundial tras Mundial, la quiniela se volvió una tradición
            establecida. Cada Mundial (1982, 1986, 1990, 1994) tenía su
            versión. Poco a poco, también empezaron a aparecer:
          </p>
          <ul>
            <li>
              <strong>Quinielas del Brasileirão:</strong> más largas, más
              desafiantes. Generalmente en grupos de trabajo.
            </li>
            <li>
              <strong>Quinielas de la Copa Libertadores:</strong> menos
              populares, pero existían para el público más entusiasta.
            </li>
            <li>
              <strong>Quinielas de la Eurocopa:</strong> raras, pero existentes
              entre aficionados de selecciones europeas específicas.
            </li>
          </ul>

          <h2>La era del Excel (los años 2000)</h2>
          <p>
            Con la popularización de la computadora personal y de Microsoft
            Office, las quinielas migraron a Excel. Surgió el papel del
            &quot;coordinador de la hoja de cálculo&quot; — aquel amigo que
            enviaba la hoja de cálculo para que todos la rellenaran, copiaba los
            pronósticos de vuelta, calculaba la puntuación con fórmulas.
          </p>
          <p>
            <strong>Era un avance enorme respecto al papel, pero tenía
            problemas</strong>: una versión de la hoja de Excel rota,
            pronósticos perdidos, fórmulas equivocadas. El coordinador también
            se llevaba el papel ingrato de explicar la puntuación en cada
            jornada.
          </p>

          <h2>La era del WhatsApp (los años 2010)</h2>
          <p>
            Con WhatsApp, la quiniela migró al grupo. Cada uno publicaba
            los pronósticos en el grupo, el organizador los anotaba en una hoja
            de cálculo, y después compartía la clasificación. <strong>Era aún
            más ágil que Excel pero el caos crecía</strong> — pronósticos
            perdidos entre 200 mensajes, dudas sobre quién pronosticó qué,
            alguien que siempre &quot;olvidaba&quot; pronosticar y después se
            quejaba.
          </p>

          <h2>La era de las apps (a partir de los años 2010 y 2020)</h2>
          <p>
            A partir de mediados de los años 2010, empezaron a surgir apps y
            sitios especializados en quinielas. <strong>Lo que entregan es
            simple pero valioso</strong>: importación automática de partidos,
            cálculo automático de la puntuación, clasificación en tiempo real,
            notificaciones de recordatorio, clasificación global entre todos los
            usuarios de la app.
          </p>
          <p>
            <strong>Bolão Elite</strong> es parte de esa última generación.
            Fue creado en 2026 por un Ingeniero de Software brasileño (fan del
            fútbol y jugador habitual de quinielas entre amigos) que quería
            resolver de forma definitiva los problemas de las generaciones
            anteriores. Conoce más en la <a href="/es/acerca">página acerca de nosotros</a>.
          </p>

          <h2>Lo que NO cambió</h2>
          <p>
            A pesar de toda la evolución tecnológica, el corazón de la quiniela
            es el mismo. <strong>Sigue tratándose de amigos reunidos en torno
            al fútbol</strong>. Las charlas de bar, las bromas por
            WhatsApp, los pronósticos locos para burlarse de quien apuesta por
            el equipo rival, la celebración en el momento del gol — todo eso
            continúa.
          </p>
          <p>
            La app es solo una herramienta. <strong>La tradición es cultural</strong>,
            y Brasil es probablemente el país que más la ha abrazado.
          </p>

          <h2>Lo que cambió (para mejor)</h2>
          <p>
            Por otro lado, la era digital trajo ventajas reales:
          </p>
          <ul>
            <li>
              <strong>Sin errores de cálculo:</strong> antes, el organizador
              podía equivocarse en la hoja de cálculo sin querer. Hoy la
              puntuación es automática — no hay espacio para la polémica.
            </li>
            <li>
              <strong>Sin pronósticos perdidos:</strong> todo registrado
              digitalmente, con marca de tiempo.
            </li>
            <li>
              <strong>Notificaciones:</strong> nadie se olvida de pronosticar
              (a menos que ignore la notificación).
            </li>
            <li>
              <strong>Clasificación en tiempo real:</strong> no hay que esperar
              a que el organizador &quot;cierre la jornada&quot;.
            </li>
            <li>
              <strong>Quiniela pública:</strong> ahora se puede competir no
              solo con amigos sino con cualquiera de Brasil.
            </li>
          </ul>

          <h2>El futuro</h2>
          <p>
            La quiniela entre amigos va a continuar. En el Mundial de 2026, en
            el Brasileirão cada año, en cualquier torneo relevante. <strong>Es
            una tradición que forma parte de la cultura brasileña del
            fútbol</strong>, y ninguna herramienta puede (ni debe)
            sustituir el aspecto humano de eso.
          </p>
          <p>
            Bolão Elite solo quiere hacer esa experiencia más fácil. Sin
            hoja de cálculo, sin cálculos, sin confusión. Juntas a los amigos,
            creas la quiniela, pronosticas, sigues la clasificación. El resto es
            fútbol.
          </p>

          <h2>Conclusión</h2>
          <p>
            Del papel madera a una aplicación, la quiniela brasileña caminó
            junto con la tecnología. Pero en el fondo, sigue tratándose de lo
            que siempre fue: amigos, fútbol, alegría, bromas. Ninguna app va a
            cambiar eso — y ninguna app debería querer cambiarlo.
          </p>
          <p>
            ¿Listo para crear la tuya? Mira la{' '}
            <a href="/es">página de inicio</a> o explora el{' '}
            <a href="/es/blog">blog completo</a> para más contenido.
          </p>
        </>
      ),
    },
  },
};
